import { useCallback, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../../components/Icon';
import { useAnchoredPopover } from '../../hooks/useAnchoredPopover';
import { useDismissableOverlay } from '../../hooks/useDismissableOverlay';

export interface MoreMenuItem {
  readonly key: string;
  readonly icon: IconName;
  readonly label: string;
}

/** #807 -- an item that does something (as opposed to a "coming soon" placeholder). */
export interface MoreMenuAction extends MoreMenuItem {
  readonly onSelect: () => void;
  readonly disabled?: boolean;
}

interface MoreMenuProps {
  /** Actions (#807: Clear drawing). */
  readonly actions?: readonly MoreMenuAction[];
  /** Placeholders, listed under "Coming soon". */
  readonly items: readonly MoreMenuItem[];
  /** #807 R4 -- the toolbar's overflowed controls, listed first. */
  readonly overflow?: ReactNode;
  /** #807 R4 -- receives the element (inside the open menu) the paper's own overflowed view
   *  controls render into; null while closed. */
  readonly overflowSlotRef?: (element: HTMLDivElement | null) => void;
  /** Controlled open state (so a command elsewhere can close the menu); uncontrolled if absent. */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  /** Receives the ⋯ button (popovers opened from the menu anchor to it). */
  readonly onTrigger?: (element: HTMLButtonElement | null) => void;
}

// UI polish review #3: the workspace toolbars carried a row of disabled "Coming soon" icons
// (3D / Notes / Style / Lock / Delete) every session, diluting the "an icon does something"
// contract. They move behind this one `⋯` button -- still discoverable as roadmap, off the bar.
//
// #512 -- positioned with `position: fixed` via useAnchoredPopover (viewport coordinates computed
// from the trigger's own boundingClientRect), not plain CSS `position: absolute` --
// `.workspace-toolbar-start` now scrolls horizontally instead of wrapping when it doesn't fit, and
// a plain absolutely-positioned popover would get clipped by that ancestor's `overflow-x`.
// `position: fixed` escapes it entirely. `.workspace-more-menu`'s own `min-width` is the width
// estimate useAnchoredPopover needs before it can measure the real thing (the menu's actual
// content -- short static labels -- never exceeds it in practice).
const MORE_MENU_ESTIMATED_WIDTH_PX = 180;

export function MoreMenu({
  actions = [],
  items,
  overflow,
  overflowSlotRef,
  open: controlledOpen,
  onOpenChange,
  onTrigger
}: MoreMenuProps) {
  const { t } = useTranslation();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean | ((current: boolean) => boolean)) => {
      const value = typeof next === 'function' ? next(open) : next;
      if (onOpenChange) onOpenChange(value);
      else setUncontrolledOpen(value);
    },
    [onOpenChange, open]
  );
  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const setTrigger = useCallback(
    (element: HTMLButtonElement | null) => {
      triggerRef.current = element;
      onTrigger?.(element);
    },
    [onTrigger]
  );
  const menuRef = useRef<HTMLDivElement | null>(null);
  const anchor = useAnchoredPopover(open, triggerRef, menuRef, () => MORE_MENU_ESTIMATED_WIDTH_PX);
  useDismissableOverlay(open, setOpen, triggerRef, menuRef);

  if (items.length === 0 && actions.length === 0 && !overflow && !overflowSlotRef) return null;

  return (
    <div className="workspace-more">
      <button
        ref={setTrigger}
        type="button"
        className="icon-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t('workspace.actionBar.moreGroup')}
        title={t('workspace.actionBar.moreGroup')}
        onClick={() => setOpen((value) => !value)}
        data-testid="more-menu-trigger"
      >
        <Icon name="moreHoriz" />
      </button>
      {open && anchor ? (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={t('workspace.actionBar.moreGroup')}
          className="workspace-more-menu"
          style={{ top: anchor.top, left: anchor.left, maxHeight: `calc(100dvh - ${anchor.top + 8}px)` }}
          data-testid="more-menu"
        >
          {/* #807 R4 -- what the toolbar row has no room for: the app's own groups, then the
              paper's view controls (portalled into the slot). Divided from the rest when non-empty. */}
          <div className="workspace-more-overflow" data-testid="more-overflow">
            {overflow}
            <div className="workspace-more-overflow-slot" ref={overflowSlotRef} />
          </div>
          {actions.map(({ key, icon, label, onSelect, disabled }) => (
            <button
              key={key}
              type="button"
              role="menuitem"
              className="workspace-more-item workspace-more-action"
              disabled={disabled}
              onClick={() => {
                setOpen(false);
                onSelect();
              }}
            >
              <Icon name={icon} size="1.1rem" />
              <span>{label}</span>
            </button>
          ))}
          {actions.length > 0 && items.length > 0 ? <hr className="workspace-more-separator" /> : null}
          {items.length > 0 ? <p className="workspace-more-heading">{t('workspace.actionBar.comingSoon')}</p> : null}
          {items.map(({ key, icon, label }) => (
            <div key={key} role="menuitem" aria-disabled="true" className="workspace-more-item">
              <Icon name={icon} size="1.1rem" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
